/**
 * POS Mobile Cart Drawer
 * A slide-up drawer for the cart on mobile devices
 * Shows item count on floating button, full cart on open
 * Includes haptic feedback and animation when cart updates
 */
import { useEffect, useRef, useState } from 'react';
import { ShoppingBag, X, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function POSMobileCartDrawer({ 
  isOpen, 
  onToggle, 
  itemCount, 
  total,
  children // The actual POSCart component (rendered conditionally by parent)
}) {
  const prevItemCount = useRef(itemCount);
  const [bounce, setBounce] = useState(false);

  // Detect when items are added and trigger feedback
  useEffect(() => {
    if (itemCount > prevItemCount.current && !isOpen) {
      // Item was added - trigger haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate(30); // Short vibration on the button
      }
      
      // Trigger bounce animation
      setBounce(true);
      const timer = setTimeout(() => setBounce(false), 300);
      return () => clearTimeout(timer);
    }
    prevItemCount.current = itemCount;
  }, [itemCount, isOpen]);

  return (
    <>
      {/* Floating Cart Button - Only visible on mobile when drawer is closed */}
      {!isOpen && (
        <button
          onClick={onToggle}
          className={`fixed bottom-20 right-4 z-40 md:hidden flex items-center gap-2 px-4 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-full shadow-lg transition-all active:scale-95 ${
            bounce ? 'animate-bounce-once' : ''
          }`}
          data-testid="mobile-cart-button"
          style={{
            animation: bounce ? 'cartBounce 0.3s ease-out' : 'none'
          }}
        >
          <ShoppingBag className="w-5 h-5" />
          {itemCount > 0 && (
            <>
              <span className="font-bold">{itemCount}</span>
              <span className="text-teal-100">|</span>
              <span className="font-semibold">${total.toFixed(2)}</span>
            </>
          )}
          {itemCount === 0 && <span>Cart</span>}
          <ChevronUp className="w-4 h-4 ml-1" />
        </button>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onToggle}
          data-testid="mobile-cart-drawer-backdrop"
        />
      )}

      {/* Slide-up Drawer */}
      <div 
        className={`fixed bottom-0 left-0 right-0 z-50 md:hidden transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{ maxHeight: '85vh' }}
      >
        {/* Drawer Container */}
        <div className="bg-white dark:bg-gray-800 rounded-t-2xl shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: '85vh' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-teal-600" />
              <span className="font-semibold text-gray-900 dark:text-white">
                Your Order
              </span>
              {itemCount > 0 && (
                <span className="px-2 py-0.5 bg-teal-600 text-white text-xs font-bold rounded-full">
                  {itemCount}
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggle}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              data-testid="mobile-cart-drawer-close"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Cart Content - Scrollable */}
          <div className="flex-1 overflow-y-auto">
            {children}
          </div>
        </div>
      </div>

      {/* CSS Keyframes for bounce animation */}
      <style>{`
        @keyframes cartBounce {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
      `}</style>
    </>
  );
}
